// Core
import { useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import { Box, Button, Flex, Grid, Text, Image, useColorMode, useColorModeValue } from '@chakra-ui/react';
import { NavLink, Link } from 'react-router-dom';

// Assets
import { apiService } from 'services/ApiService';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';
import ticket from 'assets/img/details/ticket.png';
import { ReactComponent as UpgradePlanIcon } from 'assets/svg/upgrade-plan.svg';
import placeholderPlan from 'assets/img/empty-plan.png';

// Components
import Card from 'components/Card/Card';
import { UpgradeIcon, DowngradeIcon } from 'components/Icons/Icons';

const UpgradeDowngrade = ({userData}) => { 
	const { colorMode, toggleColorMode } = useColorMode();
	const isDark = colorMode === 'dark';

	const border = useColorModeValue('1px solid #CBE7FF', '1px solid #262D47');
	const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');
	const textColorSubDowngrade = useColorModeValue('#7787B0', '#8794C2');

	const [isPanelLoading, setIsPanelLoading] = useState(false);

	const activePlan = userData?.activePlan;

    const { data, isLoading } = useQuery({
        queryKey: ['benefits-membership'],
        queryFn: async () => {
            const response = await apiService.getMemberships();
            return response.data;
        },
        refetchOnWindowFocus: false
    });

    const getImageByType = (array, type) => {
	    const foundItem = array.find(item => item.type === type);
	    return foundItem ? foundItem.image : null;
	};

	const goToCustomerPortal = useCallback(async () => {
		const id = userData?.activeSubscription?.subscriptionId;

	    setIsPanelLoading(true);
	    const response = await apiService.goToSubscriptionCustomerPortal({id});

	    if (response.message === 'Success') {
	      window.location.href = response.data;
	    } 

	    setIsPanelLoading(false);
	}, []);

	return (
		<>
		{ activePlan?.type !== 'elite' && !isLoading && (
			<Card padding="0" mt="32px" bg="transparent" border={border} boxShadow={boxShadow} overflow="hidden" position="relative">
				<Box p={{base: '16px', md: '20px 20px 12px 20px'}} maxW={{base: '100%', md: '55%'}}>
					<Text variant="header" lineHeight="100%" fontSize={{base: '36px', md: '48px'}}>
						Get even 
						<br /><Text as="span" variant="header-gradient" fontSize={{base: '36px', md: '48px'}}>
							MORE BENEFITS
							</Text>
					</Text>

					<Text mt="10px">
						Gain more advantages with upgraded membership plan!
					</Text>

					
					<Box mt="24px">
						{ activePlan?.type === 'entry' ? (
							<>
								<Flex gap="8px" position="relative" alignItems="center">
									<Image src={getImageByType(data, 'premium') ?? placeholderPlan} alt="Plan" w="36px" h="36px" />
									<Text variant="header" fontSize="40px">75%</Text>
									<UpgradePlanIcon />
								</Flex>

								<Flex mt="12px" gap="8px" position="relative" alignItems="center">
									<Image src={getImageByType(data, 'elite') ?? placeholderPlan} alt="Plan" w="36px" h="36px" />
									<Text variant="header" fontSize="40px">50%</Text>
								</Flex>
							</>
						) : (
							<Flex gap="8px" position="relative" alignItems="center">
								<Image src={getImageByType(data, 'elite') ?? placeholderPlan} alt="Plan" w="36px" h="36px" />
								<Text variant="header" fontSize="40px">50%</Text>
								<UpgradePlanIcon />
							</Flex>
						)}
						
					</Box>

					<Button 
						onClick={goToCustomerPortal}
						leftIcon={<UpgradeIcon fontSize="24px" />}
						variant={ isDark ? 'button-lg-g' : 'button-lg' } 
						maxW={{base: '100%', sm: '213px'}} 
						mt="24px">
						Upgrade Now
					</Button>
				</Box>

				<Box zIndex="-1" 
					position="absolute" 
					className="animation-account-membership"
					w="100%" h="100%">
					{ isDark && <Box 
			          display={{base: 'block', lg: 'none'}}
			          top="-60px"
			          bg="#00071C" zIndex="1" filter="blur(110px)" borderRadius="100%" w={{base: '100%', sm: '90%', lg: 'none'}} h="290px" position="absolute" /> }


					<Box position="absolute" 
						bg="#099BFF" 
						filter="blur(100px)" 
						w="200px" h="200px" position="absolute" 
						top="-250px" right="-10%" />
					
					{ isDark ? <Lines className="abstract-lines" style={{ left: '30%', top:'-330px'}} /> 
	    			: <LinesWhite className="abstract-lines" style={{ left: '30%', top:'-330px', opacity: 0.3 }} /> }

					<Image 
						position="absolute" 
						src={ticket} 
						alt="ticket" 
						maxW="420px" 
						w="100%" 
						h="auto" 
						className="animation-account-membership-ticket1"
						transform="rotate(45deg)" 
						bottom="-60px"
						right="-50px" />

					<Image 
						position="absolute" 
						src={ticket} 
						alt="ticket" 
						maxW="190px" 
						w="100%" 
						h="auto" 
						className="animation-account-membership-ticket2"
						transform="rotate(-25deg)" 
						top="-30%"
						right="-50px" />
				</Box>
		    </Card>
		)}

		{ activePlan?.type !== 'entry' && !isLoading && (
			<Card padding="12px" mt="16px" bg="transparent" border={border} boxShadow={boxShadow} overflow="hidden" position="relative">
				<Grid templateColumns={{ base: '1fr', lg: '1fr 213px' }} templateRows="1fr" gap="16px" alignItems="center">
					<Box>
						<Flex gap="12px" alignItems="center">
							<Box
								p="4px"
								borderRadius="8px"
								bg="rgba(255, 59, 48, 0.10)"
								>
								<DowngradeIcon fontSize="24px" color="#FF3B30" />
							</Box>
							<Text variant="title-lg" fontSize="20px">Get the plan that fits you</Text>
						</Flex>
						<Text mt="8px" variant="text-sm" fontSize="15px" color={textColorSubDowngrade}>Adjust your membership level to match your current goals and needs.</Text>
					</Box>

					<Button variant="solid-md" onClick={goToCustomerPortal} leftIcon={<DowngradeIcon fontSize="24px" />}>Downgrade</Button>
				</Grid>
			</Card>
		)}
	    </>
	);
};

export default UpgradeDowngrade;